import { useEffect, useRef, useState } from 'react'
import {
  AtendimentoEstado,
  ESTADOS_ATENDIMENTO_FIXOS,
  IconPaths,
  stringifyRequestQueryResumo,
} from '../../../../../../../_metronic/helpers'
import { StatCards, StatusDotTone } from '../../../../../../components'
import { EstadoAtendimentoList } from '../../../../atendimento/core/_models'
import { getResumoAtendimentosProfissional } from '../../core/_requests'
import { useQueryRequest } from '../../core/QueryRequestProvider'
import { useQueryResponse } from '../../core/QueryResponseProvider'

type Props = {
  estado: number
  onChange: (estado: number) => void
}

/** Ícone e cor de cada estado (um por estado; o rótulo carrega o significado). */
const ESTADO_ICONE: Record<AtendimentoEstado, { icon: IconPaths; tone: StatusDotTone }> = {
  [AtendimentoEstado.Aguardando]: { icon: IconPaths.AtendimentoAguardando, tone: 'info' },
  [AtendimentoEstado.EmAtendimento]: { icon: IconPaths.AtendimentoEmAtendimento, tone: 'primary' },
  [AtendimentoEstado.Pendente]: { icon: IconPaths.AtendimentoPendencia, tone: 'warning' },
  [AtendimentoEstado.RetornoPendencia]: {
    icon: IconPaths.AtendimentoPendenciaResolvida,
    tone: 'dark',
  },
  [AtendimentoEstado.Finalizado]: { icon: IconPaths.AtendimentoFinalizado, tone: 'success' },
  [AtendimentoEstado.Falta]: { icon: IconPaths.AtendimentoNaoCompareceu, tone: 'danger' },
  [AtendimentoEstado.Cancelado]: { icon: IconPaths.AtendimentoCancelado, tone: 'neutral' },
}

/**
 * Rótulo curto do card: "Atendimento com pendência" → "Pendência", "Atendimento finalizado" →
 * "Finalizado", "Aguardando atendimento" → "Aguardando", "Pendência resolvida" →
 * "Pend. Resolvida". "Em atendimento" fica como está.
 */
const rotuloEstado = (descricao?: string) =>
  descricao
    ?.replace(/^Atendimento (com )?/i, '')
    .replace(/^Aguardando atendimento/i, 'Aguardando')
    .replace(/^Pendência resolvida/i, 'Pend. Resolvida')
    .replace(/Paciente/gi, '')
    .trim()
    .replace(/^\w/, (c) => c.toUpperCase())

/**
 * Cards de estado do atendimento (com contagem), acima da tabela. Selecionar um card filtra a
 * listagem; as contagens seguem os demais filtros e pulsam enquanto recarregam.
 */
export const AtendimentoEstadoCards = ({ estado, onChange }: Props) => {
  const { state } = useQueryRequest()
  const { response } = useQueryResponse()
  const [estadosAtendimento, setEstadosAtendimento] = useState<
    { estado: EstadoAtendimentoList; qtd: number | null }[]
  >(ESTADOS_ATENDIMENTO_FIXOS.map((e) => ({ estado: e, qtd: null })))

  // Nova busca: contagens voltam a "carregando" até o resumo chegar.
  useEffect(() => {
    setEstadosAtendimento((prev) => prev.map((item) => ({ ...item, qtd: null })))
  }, [state.search])

  // O resumo acompanha a chegada da resposta da listagem (não cada mudança de filtro, que já
  // dispara a listagem); `state` entra por ref para ler o valor corrente sem reexecutar o efeito.
  const stateRef = useRef(state)
  stateRef.current = state

  useEffect(() => {
    const fetchResumosAtendimento = async () => {
      const resumo = await getResumoAtendimentosProfissional(
        stringifyRequestQueryResumo(stateRef.current)
      )

      setEstadosAtendimento(
        Object.entries(resumo).map(([descricao, item]) => ({
          estado: {
            ativo: true,
            descricao,
            nome: item?.nome ?? descricao,
            id: item?.id ?? 0,
          },
          qtd: item?.valor ?? 0,
        }))
      )
    }

    fetchResumosAtendimento()
  }, [response])

  return (
    <StatCards
      ariaLabel='Estado do atendimento'
      activeId={estado}
      onSelect={(id) => onChange(Number(id))}
      items={estadosAtendimento.map(({ estado: item, qtd }) => ({
        id: Number(item.id),
        label: rotuloEstado(item.descricao) ?? item.nome,
        value: qtd,
        isLoading: qtd === null,
        icon: ESTADO_ICONE[Number(item.id) as AtendimentoEstado]?.icon,
        iconTone: ESTADO_ICONE[Number(item.id) as AtendimentoEstado]?.tone,
      }))}
    />
  )
}
